import {HttpClient} from '@angular/common/http';
import {Observable, of, ReplaySubject, shareReplay, take} from 'rxjs';
import moment from "moment/moment";
import {environment} from '../../environments/environment';
import {Gateway} from "./gateway";
import {HandlerRegistry} from "./handler-registry.service";
import {HandlerInvoker, HandlerOptions} from './handler';
import {ElementRef} from '@angular/core';
import {tap} from 'rxjs/operators';
import {AppCommonUtils} from './app-common-utils';

export abstract class RequestGateway extends Gateway {
  private cache: Map<string, ReplaySubject<any>> = new Map();

  protected constructor(invokers: Map<string, HandlerInvoker[]>, protected registry: HandlerRegistry,
                        private http: HttpClient, private path: string) {
    super(invokers, registry);
  }

  send(type: string, payload: any = {}, options: RequestOptions = {}, elementRef?: ElementRef<Element>): Observable<any> {
    const localResult = this.tryHandleRequestLocally(type, payload, options, elementRef);
    if (localResult) {
      return localResult;
    }
    let o: Observable<any>;
    if (options.caching) {
      const key = type + JSON.stringify(payload);
      const cachedResponse = this.cache.get(key);
      if (cachedResponse) {
        return cachedResponse;
      }
      const subject = o = new ReplaySubject(1);
      this.cache.set(key, subject);
      this.doSend(type, payload, options).pipe(shareReplay(1)).subscribe(
        {
          next: n => subject.next(n),
          error: e => {
            this.cache.delete(key);
            subject.error(e);
          }
        });
    } else {
      o = this.doSend(type, payload, options);
    }
    if (!options.hideError) {
      o = o.pipe(tap({error: e => AppCommonUtils.registerError(e)}));
    }
    if (options.showSpinner) {
      o = AppCommonUtils.waitForProcess(o);
    }
    return o;
  }

  protected tryHandleRequestLocally(type: string, payload: any, options: RequestOptions = {}, elementRef?: ElementRef<Element>): Observable<any> {
    const domResult = this.tryHandleOnDom(type, payload, elementRef);
    if (domResult) {
      return this.handleResult(type, payload, domResult.result, domResult.handler.options);
    }
    const localHandler = this.getLocalHandler(type);
    if (localHandler) {
      console.debug(`Locally handling request ${type}. Handler: `, localHandler);
      payload = this.getPayload(payload);
      const result = localHandler.invoker.method.apply(localHandler.instance, [payload, options]);
      return this.handleResult(type, payload, result instanceof Observable ? result : of(result), localHandler.invoker.options);
    }
  }

  private handleResult(type: string, payload: any, result: Observable<any>, options: HandlerOptions): Observable<any> {
    if (options?.caching) {
      const key = type + JSON.stringify(payload);
      const cachedResponse = this.cache.get(key);
      if (cachedResponse) {
        return cachedResponse;
      }
      const subject = new ReplaySubject(1);
      result.pipe(shareReplay(1)).subscribe({
        next: n => subject.next(n),
        error: e => {
          this.cache.delete(key);
          subject.error(e);
        }
      });
      this.cache.set(key, subject);
      return subject as Observable<any>;
    }
    return result;
  }

  private doSend(type: string, payload: any, options: any = {}) {
    const message = asMessage(payload, type);
    message.metadata.localTime = moment().toLocaleString();
    options.withCredentials = true;
    const diagnosticMessage = message.payload?.sourceFile ? {...message, payload: {...message.payload,
      sourceFile: {fileName: message.payload.sourceFile.fileName, archivedContent: true}}} : message;
    console.debug("sending request", type, diagnosticMessage);
    options.headers = addHeadersFromLocalStorage(options.headers, 'Authorization', 'X-Impersonation');
    return this.http.post<any>(environment.apiProtocol + environment.apiDomain + this.path, message, <any>options);

    function addHeadersFromLocalStorage(headers : any, ...headerNames : string[]): any {
      headers = headers || {};
      headerNames.forEach(headerName => {
        if (headerName && !headers[headerName]) {
          const value = localStorage.getItem(headerName);
          if (value) {
            headers[headerName] = value;
          }
        }
      });
      return headers;
    }
  }

  removeFromCache(type: string, payload?: any) {
    let key = type;
    if (payload) {
      key += JSON.stringify(payload);
    }
    Array.from(this.cache.keys()).filter(k => k.startsWith(key)).forEach(k => this.cache.delete(k));
  }

  clearCache() {
    this.cache = new Map();
  }

  modifyCache<T>(type: string, modifier: (T) => T) {
    Array.from(this.cache.entries())
      .filter(entry => entry[0].startsWith(type))
      .map(entry => entry[1])
      .forEach(o => o.pipe(take(1)).subscribe(value => o.next(modifier(value))));
  }
}

function asMessage(payload: any, type: string) {
  if (!payload) {
    payload = {};
  }
  //payload is already a message
  if (payload['@class'] === 'io.fluxzero.sdk.common.Message') {
    payload.metadata = payload.metadata || {};
    payload.payload["@class"] = type;
    return payload;
  }

  //payload should be converted to a message
  payload["@class"] = type;
  return {
    "@class": 'io.fluxzero.sdk.common.Message',
    payload: payload,
    metadata: {}
  }
}

export interface RequestOptions {
  caching?: boolean;
  showSpinner?: boolean;
  hideError?: boolean;
  responseType?: XMLHttpRequestResponseType;
  headers?: any;
}
